// jshint curly:true, maxdepth:5, unused:false
import app from 'app';
import api from './api';

function BGMusicAnalytics(store) {
  store.subscribe((mutation, state) => {
    if ('initialize' === mutation.type) {
      const options = mutation.payload;
      app.analytics('analytics:timeline:openedBGMusic', {
        'Timeline Type': options.storyHasNarration ? 'Text' : 'Time',
      });
    } else if ('destroy' === mutation.type) {
      const options = mutation.payload;
      app.analytics('analytics:timeline:closedBGMusic', {
        'Timeline Type': options.storyHasNarration ? 'Text' : 'Time',
        Source: options.hitCloseButton ? 'button' : 'x-icon|escape',
      });
    } else if ('deleteAudioResult' === mutation.type) {
      const audio = mutation.payload;
      app.analytics('analytics:timeline:deletedBGMusic', {
        'Track Name': audio.metadata.title,
      });
    } else if (
      'setAsFavorite' === mutation.type &&
      mutation.payload.favorite &&
      !mutation.payload.silent
    ) {
      const audio = mutation.payload.audio;
      app.analytics('analytics:timeline:favoritedBGMusic', {
        'Track Name': audio.metadata.title,
      });
    } else if ('setPlaying' === mutation.type) {
      const audio = mutation.payload;
      if (audio) {
        app.analytics('analytics:timeline:playedBGMusic', {
          'Track Name': audio.metadata.title,
        });
      }
    } else if ('setSelected' === mutation.type) {
      const { before, after } = mutation.payload;
      if (after) {
        app.analytics('analytics:timeline:replacedBGMusic', {
          'Track Name': after.metadata.title,
          Storyboard: !!state.options.isVideoTemplate,
        });
      } else if (before && null === after) {
        app.analytics('analytics:timeline:unselectedBGMusic', {
          'Track Name': before.metadata.title,
        });
      }
    } else if ('addedToUploadQueue' === mutation.type) {
      const rawAudio = mutation.payload;
      app.analytics('analytics:timeline:uploadBGMusic', {
        'File Type': rawAudio.blob._fileExt,
      });
    } else if ('setSortOrder' === mutation.type) {
      const fieldName = mutation.payload.value;
      app.analytics('analytics:timeline:sortBGMusic', {
        Field: fieldName,
      });
    } else if ('setFilter' === mutation.type) {
      const newFilter = mutation.payload;
      const filterName = Object.keys(newFilter).join('|');

      if ('query' in newFilter && !_.isEmpty(newFilter.query)) {
        app.analytics('analytics:timeline:searchedBGMusic', {
          Query: newFilter.query,
        });
      } else {
        const value = Object.values(newFilter).join('|');
        app.analytics('analytics:timeline:filterBGMusic', {
          Filter: filterName,
          Value: value,
        });
      }
    }
  });
}

export default {
  plugins: [BGMusicAnalytics],

  /**
   * In strict mode, whenever Vuex state is mutated outside of mutation handlers,
   * an error will be thrown. This ensures that all state mutations can be
   * explicitly tracked by debugging tools.
   *
   * Set to false cause audio upload handled outside of store
   */
  strict: false,

  state() {
    const defaultFilters = {
      favorite: false,
      mine: false,
      mood: '',
      query: '',
      sortBy: 'creationDate',
      asc: false,
    };

    return {
      /** bg music pace [tempo] (not in bpm, index goes from 1 to 5) */
      PACE_MAP: [
        'Reserved',
        'Slow',
        'Slow Medium',
        'Medium',
        'Medium High',
        'High',
      ],

      /** when to display tooltip - if text is longer then this (in pixels) */
      MAX_TITLE_WIDTH: 248,

      /** init options (not expected to be changed)*/
      options: {},

      /* fetch results */
      gallery: [],
      /* currently uploading items */
      uploading: [],
      selected: null,
      /* currently playing audio */
      playing: null,

      /* search params */
      filter: _.cloneDeep(defaultFilters),
      defaultFilters: defaultFilters,
      sortByFieldOrderDefaults: {
        title: true,
        creationDate: false,
        genre: true,
        mood: true,
        pace: true,
      },
      supportedFilters: {
        genres: [],
        moods: [],
      },

      /* populating results-list with data, relevant to current set of filters*/
      hydrating: false,
      fetching: false,
      endOfList: false,

      /* pagination */
      ITEMS_IN_PAGE: 60,
      page: 0,
    };
  },

  // @params state, getters
  getters: {
    selectedId(state) {
      return api.getId(state.selected);
    },
    playingId(state) {
      return api.getId(state.playing);
    },
  },

  // @param state
  // @param payload
  mutations: {
    initialize(state, { selected, options }) {
      state.options = options;
      state.selected = selected;
    },

    destroy(state) {
      state.uploading.splice(0);
    },

    resetFiltersToDefault(state) {
      Object.assign(state.filter, _.cloneDeep(state.defaultFilters));
    },

    setFilter(state, newFilter) {
      Object.assign(state.filter, newFilter);
    },

    setGallery(state, gallery) {
      state.gallery = gallery;
    },

    deleteAudioResult(state, audio) {
      const i = state.gallery.indexOf(audio);
      if (i >= 0) {
        state.gallery.splice(i, 1);
      }
    },

    startSearch(state, options) {
      if (options.more) {
        ++state.page;
        state.startItem = state.page * state.ITEMS_IN_PAGE;
      } else {
        state.gallery = [];
        state.endOfList = false;
        state.page = 0;
        state.startItem = 0;
        state.hydrating = true;
      }

      state.fetching = true;
    },

    parseSearch(state, { results }) {
      _.each(results, (audio, i) => {
        audio._serverOrder = state.page + i + 1;
        // create property if not defined
        app.Vue.set(audio, 'favorite', audio.favorite || false);

        // track foreign upload progress
        if (
          audio.metadata.uploadStatus === app.uploadStatus.PROCESSING &&
          !app.UploadQueue.isUploading(api.getId(audio))
        ) {
          api.foreignUpload.track(audio);
        }
      });

      // filter-out uploading from regular results
      results = _.filter(results, (audio) => {
        const isUploading = _.find(state.uploading, (uploadingAudio) => {
          return api.getId(uploadingAudio) === api.getId(audio);
        });
        return !isUploading;
      });

      if (_.isEmpty(results)) {
        state.endOfList = true;
      } else {
        state.endOfList = results.length < state.ITEMS_IN_PAGE;
        state.gallery = state.gallery.concat(results);
      }
    },

    finishSearch(state) {
      state.fetching = false;
      state.hydrating = false;
    },

    setSelected(state, { after }) {
      state.selected = after;
    },

    /**
     * @param state
     * @param audio {null|Object}
     */
    setPlaying(state, audio) {
      state.playing = audio;
    },

    setAsFavorite(state, { audio, favorite }) {
      audio.favorite = favorite;
      // HACK: music.vue doesn't pick-up the change in favorite, but this trick helps it to.
      audio.metadata.title += ' ';
    },

    setSortOrder(state, { value, asc }) {
      state.filter.sortBy = value;
      state.filter.asc = asc;
    },

    addedToUploadQueue() {
      // although, currently, it doesn't mutates state
      // there is analytics plugin
    },

    // add to uploading
    unshiftUploading(state, audio) {
      state.uploading.unshift(audio);

      state.uploading.sort((a, b) => {
        const aTitle = a.metadata.title || '';
        const bTitle = b.metadata.title || '';
        // asc
        return aTitle.localeCompare(bTitle);
      });
    },

    // move from uploading to results
    shiftUploading(state, finishedAudio) {
      state.uploading = _.filter(state.uploading, (audio) => {
        return api.getId(finishedAudio) !== api.getId(audio);
      });

      state.gallery.unshift(finishedAudio); // without sorting
    },

    setSupportedFilters(state, { genres, moods }) {
      state.supportedFilters.genres = genres;
      state.supportedFilters.moods = moods;
    },
  },

  // @param context {{state, rootState, commit, dispatch, getters, rootGetters}}
  // @param payload
  actions: {
    INITIALIZE({ commit, dispatch }, payload) {
      commit('initialize', payload);
      commit('resetFiltersToDefault');
      commit('setPlaying', null);
      dispatch('FETCH_SUPPORTED_FILTERS');
      return dispatch('SEARCH', { abort: false });
    },

    DESTROY({ commit }, payload) {
      app.api.backgroundMusic.abort();
      api.foreignUpload.clear();
      commit('destroy', payload);
    },

    CHANGE_FILTER({ commit, dispatch }, newFilter) {
      commit('setFilter', newFilter);
      commit('setPlaying', null);
      return dispatch('SEARCH');
    },

    SET_SORT_ORDER({ state, commit, dispatch }, value) {
      if (state.filter.sortBy === value) {
        // flip order if already sorted by same field
        commit('setSortOrder', { value, asc: !state.filter.asc });
      } else {
        // set new field, with default sort order
        commit('setSortOrder', {
          value,
          asc: state.sortByFieldOrderDefaults[value],
        });
      }

      commit('setPlaying', null);
      return dispatch('SEARCH');
    },

    SEARCH_ALL({ commit, dispatch }) {
      commit('resetFiltersToDefault');
      commit('setPlaying', null);
      return dispatch('SEARCH');
    },

    SEARCH({ state, commit }, options) {
      return new Promise((resolve, reject) => {
        options = _.options(options, {
          abort: true,
          more: false,
        });

        if (
          (state.fetching && !options.abort) ||
          (options.more && state.endOfList)
        ) {
          return;
        }

        commit('startSearch', options);

        if (!options.more) {
          commit('setPlaying', null);
        }

        options.abort && app.api.backgroundMusic.abort();
        const cp = app.api.backgroundMusic.find({
          // authenticators
          user: state.options.userId,
          prog: state.options.programmerId,
          // filter query
          q: state.filter.query,
          sortBy: state.filter.sortBy,
          asc: state.filter.asc,
          mood: state.filter.mood,
          isfavorite: state.filter.favorite,
          searchPrivateAssets: state.filter.mine,
          // pagination
          startItem: state.startItem,
          numberOfItems: state.ITEMS_IN_PAGE,
        });
        cp.then(
          (results) => {
            commit('parseSearch', { options, results });
            resolve();
          },
          ({ error }) => {
            app.doctorController.report(`FAILED bgmusic search: ${error}`);
            reject();
          }
        );
        cp.finally(() => {
          commit('finishSearch', options);
        });
      });
    },

    FETCH_SUPPORTED_FILTERS({ state, commit }) {
      return new Promise((resolve, reject) => {
        api.getGenres(state.options.programmerId).then((res) => {
          commit('setSupportedFilters', {
            genres: res.genres,
            moods: res.moods,
          });
          resolve();
        }, reject);
      });
    },

    DELETE({ commit, getters }, audio) {
      const rv = new _.ColdPromise();

      if (api.getId(audio) === getters.playingId) {
        commit('setPlaying', null);
      }

      app.api.backgroundMusic.delete(api.getId(audio)).finally(() => {
        commit('deleteAudioResult', audio);
        rv.resolve();
      }, rv.reject);

      return rv;
    },

    TOGGLE_FAVORITE({ state, commit }, { audio, favorite }) {
      const rv = new _.ColdPromise();

      app.api.backgroundMusic
        .favorite(api.getId(audio), favorite)
        .catch(({ error }) => {
          app.doctorController.report(`FAILED bgmusic favor: ${error}`);
        })
        .then(() => {
          if (api.getId(audio) === api.getId(state.selected)) {
            commit('setAsFavorite', {
              audio: state.selected,
              favorite,
              silent: true,
            });
          }
          commit('setAsFavorite', { audio, favorite });
        })
        .then(rv.resolve, rv.reject);

      return rv;
    },

    TOGGLE_SELECT({ state, commit, getters }, audio) {
      return new Promise((resolve) => {
        const audioId = api.getId(audio);
        const selectedAudio = getters.selectedId === audioId ? null : audio;

        commit('setSelected', {
          before: state.selected,
          after: selectedAudio,
        });
        if (getters.playingId !== audioId) {
          commit('setPlaying', audio);
        }
        if (!selectedAudio) {
          commit('setPlaying', null);
        }

        resolve();
      });
    },

    TOGGLE_PLAYING({ commit, getters }, audio) {
      return new Promise((resolve) => {
        const audioId = api.getId(audio);
        commit('setPlaying', getters.playingId === audioId ? null : audio);
        resolve();
      });
    },

    /**
     * @param state
     * @param commit
     * @param e {Event}
     * @return {ColdPromise}
     */
    ADD_MORE({ state, commit }, e) {
      const rv = new _.ColdPromise();

      const cp = api.getFileList(e);
      cp.catch((bug) => {
        console.info('chooseFiles, rejected');
        if (bug && bug instanceof Error) {
          app.doctorController.report('ADD_MORE bgmusic', bug);
        }

        rv.reject(bug);
      });
      cp.then((files) => {
        console.info('chooseFiles, resolved');
        for (let n = 0, N = files.length; n < N; n++) {
          const model = api.getUploadQueueModel(
            files[n],
            state.options.programmerId
          );
          app.UploadQueue.upload(model);
          commit('addedToUploadQueue', model.attributes);
        }

        rv.resolve();
      });
      cp.finally(() => {
        // reset file selection, so it forget
        e.target.value = null;
      });

      return rv;
    },

    /**
     * uploading started
     */
    UNSHIFT_UPLOADING({ commit }, audio) {
      commit('unshiftUploading', audio);
    },

    /**
     * upload finished
     */
    SHIFT_UPLOADING({ state, commit }, audio) {
      commit('setAsFavorite', { audio, favorite: false });
      commit('shiftUploading', audio);

      // auto-select successfully uploaded
      if (audio.metadata.uploadStatus === app.uploadStatus.DONE) {
        commit('setSelected', {
          before: state.selected,
          after: audio,
        });
      }
    },
  },
};
